import { useEffect, useRef, useState } from 'react';
import { Model } from 'type/type';
import { getModelAll } from 'api/api';
import Pagenation from 'components/common/Pagenation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CommonTable, CommonTableWrap, FilterContentContainer } from 'pages/Layout';
import { PrimaryButton } from 'components/common/Button';
import { formattedDateTime } from 'pages/utils/commonUtils';

interface SearchInputType {
  key: string,
  value: string,
  type: string,
  page?: number,
};

function RecorderModelList() {
  const isFirstRender = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // 필터obj
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    key: searchParams.get("key") || "name",
    type: searchParams.get("type") || "",
    value: searchParams.get("value") || "",
    page: searchParams.get("page") ? Number(searchParams.get("page")) : undefined,
  });
  const navigate = useNavigate();
  const [recorderModelList, setRecorderModelList] = useState<Model[]>([]);
  // 페이지네이션
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [page, setPage] = useState(searchObj.page || 1); // 현재 페이지
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [totalCount, setTotalCount] = useState(-1);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자

  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
    if (str === 'prev') handlePrevPage();
    else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
    const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
    setStartButtonNum(prevPage);
    setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
    const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
    setStartButtonNum(nextPage);
    setPage(nextPage);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') searchHandle();
  }

  const searchHandle = () => {
    page === 1 ? getRecorderModel(page, limit) : setPage(1);
  }

  const getRecorderModel = async (page: number, limit: number) => {
    const { key, value, type } = searchObj;
    const param = {
      [key]: value,
      ...(type && { type }) // state가 있을 때만 추가
    };
    const modelRes = await getModelAll(page, limit, param);
    if (modelRes.code === 200) {
      setRecorderModelList(modelRes.data);
      setTotalCount(modelRes.total_count);
    }
    setSearchParams({ key, value, type, page: String(page) });
  };
  
  // 검색 type 변경
  const handleSearchType = (type: string) => {
    setSearchObj(prev => ({...prev, type }));
  };
  // 검색 key 변경
  const handleSearchKey = (key: string) => {
    setSearchObj(prev => ({...prev, key, value: "" }));
  };
  // 검색 value 변경
  const handleSearchValue = (value: string) => {
    setSearchObj(prev => ({...prev, value}));
  };
  
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const hasParams = searchParams.toString() !== "";
      if (!hasParams) return; // ❌ 파라미터 없으면 첫 렌더 스킵
    }
    getRecorderModel(page, limit); // ✅ 이후에는 무조건 실행
  }, [page, limit]);
  return (
    <>
      <FilterContentContainer>
        <div className='flex-row'>
          <label>검색</label>
          <div>
            <select name="search_type" onChange={(e) => handleSearchKey(e.target.value)} value={searchObj.key} >
              <option value="name">Model명</option>
              <option value="vendor">제조사</option>
            </select>
          </div>
          <div>
            <input type="text" name="search_value" onChange={(e) => handleSearchValue(e.target.value)} value={searchObj.value} placeholder="검색 내용 입력" onKeyUp={handleKeyDown} />
          </div>
        </div>
        <div className='flex-row'>
          <label>구분</label>
          <div>
            <select name="search_state" onChange={(e) => handleSearchType(e.target.value)} value={searchObj.type} >
              <option value={''}>--전체--</option>
              <option value={'NVR'}>NVR</option>
              <option value={'DVR'}>DVR</option>
            </select>
          </div>
        </div>
        <PrimaryButton type="button" onClick={searchHandle}>조회</PrimaryButton>
        <div className='flex-row btn-wrap'>
          <PrimaryButton type="button" onClick={() => navigate('process')}>등록</PrimaryButton>
        </div>
      </FilterContentContainer>
      <div>
        검색 결과: <span style={{ fontWeight: "600" }}>{totalCount < 0 ? 0 : totalCount}</span>건
      </div>
      <CommonTableWrap>
        <CommonTable>
          <thead>
            <tr>
              <th>No</th>
              <th>Model 명</th>
              <th>구분</th>
              <th>채널 수</th>
              <th>제조사</th>
              <th>생성 일자</th>
            </tr>
          </thead>
          <tbody>
            {totalCount === 0 ?
              <tr>
                <td colSpan={6}>검색결과가 없습니다.</td>
              </tr>
              :
              recorderModelList.map((model, idx) => 
                <tr key={`model_${idx}`} onClick={() => navigate(`process/${model.idx}`)}>
                  <td>{(page - 1) * 10 + idx + 1}</td>
                  <td>{model.name}</td>
                  <td>{model.type}</td>
                  <td>{model.channel}</td>
                  <td>{model.vendor}</td>
                  <td>{formattedDateTime(model.create_date)}</td>
                </tr>
              )
            }
          </tbody>
        </CommonTable>
      </CommonTableWrap>
      <Pagenation
        totalCount={totalCount}
        total={numPages}
        limit={limit}
        page={page}
        setPage={setPage}
        startButtonNum={startButtonNum}
        setClickButton={isClickButton}
      />
    </>
  );
}

export default RecorderModelList;